import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { BarChart3 } from "lucide-react";

export function CommitmentTerm() {
  const [p, setP] = useState(null);
  const [accept, setAccept] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(()=>{ api.get("/professionals/me").then(r=>{ setP(r.data); setAccept(!!r.data.commitment_accepted); }); }, []);
  const submit = async () => {
    if (!accept) return toast.error("Aceite o termo para prosseguir");
    setLoading(true);
    try {
      await api.post("/professionals/me/commitment");
      toast.success("Termo aceito e registrado.");
      const r = await api.get("/professionals/me"); setP(r.data);
    } catch { toast.error("Erro ao registrar aceite"); }
    finally { setLoading(false); }
  };
  return (
    <Card className="p-6 gs-shadow space-y-4" data-testid="commitment-term">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-[#0D7C66] font-semibold">Termo de Compromisso</div>
        {p?.commitment_accepted && <Badge className="bg-[#E6F4F1] text-[#0D7C66] border-0">Aceito em {new Date(p.commitment_accepted_at).toLocaleString("pt-BR")}</Badge>}
      </div>
      <h2 className="text-2xl font-extrabold" style={{fontFamily:"Plus Jakarta Sans"}}>Compromisso com os descontos aos associados</h2>
      <div className="text-sm text-slate-700 leading-relaxed space-y-2 max-h-72 overflow-auto pr-2">
        <p>Ao aderir à rede GIRO+SAÚDE, o(a) profissional/clínica se compromete a:</p>
        <p><b>1. Aplicar integralmente</b> os descontos, condições e benefícios divulgados aos associados portadores da carteira digital ativa, sem cobrança de acréscimos indevidos.</p>
        <p><b>2. Respeitar a vigência</b> de cada benefício publicado, mantendo as regras informadas na plataforma até o fim do período divulgado.</p>
        <p><b>3. Não condicionar</b> a aplicação do desconto à contratação de outros serviços não descritos no benefício.</p>
        <p><b>4. Atender o associado</b> com a mesma qualidade técnica e ética oferecida a qualquer paciente, respeitando o código de ética da sua profissão.</p>
        <p><b>5. Informar previamente</b> ao GIRO+SAÚDE sobre alteração, suspensão ou cancelamento de qualquer benefício.</p>
        <p><b>6. Manter atualizados</b> os dados de contato, endereço e serviços oferecidos.</p>
        <p><b>7. Reconhecer</b> que o GIRO+SAÚDE é plataforma de conexão e divulgação — não realiza diagnóstico, prescrição ou atendimento clínico, e não se responsabiliza por atos profissionais de sua exclusiva competência.</p>
        <p><b>8. Autorizar</b> a divulgação do seu perfil, serviços e benefícios nos canais da plataforma, respeitadas as diretrizes da LGPD.</p>
        <p>O descumprimento poderá resultar em suspensão do perfil, remoção de benefícios ou encerramento do cadastro.</p>
      </div>
      <label className="flex items-start gap-2 text-sm text-slate-700">
        <Checkbox data-testid="commitment-checkbox" checked={accept} onCheckedChange={v=>setAccept(!!v)} disabled={p?.commitment_accepted}/>
        <span>Li e aceito integralmente o Termo de Compromisso de Descontos GIRO+SAÚDE.</span>
      </label>
      {!p?.commitment_accepted && (
        <Button data-testid="commitment-submit" onClick={submit} disabled={loading || !accept} className="bg-[#0D7C66] hover:bg-[#074E41]">{loading?"Registrando...":"Registrar aceite"}</Button>
      )}
    </Card>
  );
}

export function AdminSearchReport() {
  const [d, setD] = useState(null);
  useEffect(()=>{ api.get("/admin/search-report").then(r=>setD(r.data)); }, []);
  if (!d) return <Card className="p-6 gs-shadow">Carregando…</Card>;
  const max = Math.max(1, ...d.by_specialty.map(x=>x.count));
  return (
    <div className="space-y-6" data-testid="admin-search-report">
      <Card className="p-5 gs-shadow">
        <div className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#0D7C66]"/><div className="font-bold">Buscas totais: {d.total}</div></div>
      </Card>
      <Card className="p-5 gs-shadow">
        <div className="font-bold mb-3">Especialidades mais buscadas</div>
        {d.by_specialty.length === 0 && <div className="text-sm text-slate-500">Ainda não há buscas registradas.</div>}
        <div className="space-y-2">
          {d.by_specialty.map(r=>(
            <div key={r.specialty} data-testid={`rep-spec-${r.specialty}`}>
              <div className="flex justify-between text-sm"><span>{r.specialty}</span><b>{r.count}</b></div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-1"><div className="h-full bg-[#0D7C66]" style={{width: `${(r.count/max)*100}%`}}/></div>
            </div>
          ))}
        </div>
      </Card>
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5 gs-shadow">
          <div className="font-bold mb-3">Termos digitados</div>
          {d.by_query.length===0 && <div className="text-sm text-slate-500">—</div>}
          <ul className="text-sm space-y-1">{d.by_query.map(r=><li key={r.query} className="flex justify-between border-b border-slate-100 py-1"><span>{r.query}</span><b>{r.count}</b></li>)}</ul>
        </Card>
        <Card className="p-5 gs-shadow">
          <div className="font-bold mb-3">Cidades mais pesquisadas</div>
          {d.by_city.length===0 && <div className="text-sm text-slate-500">—</div>}
          <ul className="text-sm space-y-1">{d.by_city.map(r=><li key={r.city} className="flex justify-between border-b border-slate-100 py-1"><span>{r.city}</span><b>{r.count}</b></li>)}</ul>
        </Card>
      </div>
    </div>
  );
}
